import {useEffect} from 'react'

import {getSlideStride} from './helpers'

/* Autoplay loop with pause behaviors — pauses on pointer-over (covers desktop
 * hover AND in-progress touches), focus inside any slide, and tab hidden
 * (Page Visibility). When `loop` is on, the wrap-around is delegated to
 * useInfiniteLoop; autoplay just keeps advancing and the loop hook re-snaps
 * invisibly. When `loop` is off, autoplay rewinds to the start at the end. */
export const useAutoplay = (scrollRef, {enabled, delay, loop, tilesToScroll, slidesLength}) => {
    useEffect(() => {
        if (!enabled || slidesLength <= 1) return undefined
        const node = scrollRef.current
        if (!node) return undefined

        let paused = false

        const tick = () => {
            if (paused || document.hidden) return
            const stride = getSlideStride(node) * tilesToScroll
            if (loop) {
                node.scrollBy({left: stride, behavior: 'smooth'})
                return
            }
            const {scrollLeft, scrollWidth, clientWidth} = node
            const atEnd = scrollLeft + clientWidth >= scrollWidth - 1
            if (atEnd) {
                node.scrollTo({left: 0, behavior: 'smooth'})
            } else {
                node.scrollBy({left: stride, behavior: 'smooth'})
            }
        }

        const id = setInterval(tick, delay)
        const pause = () => {
            paused = true
        }
        const resume = () => {
            paused = false
        }
        const onVisibility = () => {
            paused = document.hidden
        }

        node.addEventListener('pointerenter', pause)
        node.addEventListener('pointerleave', resume)
        node.addEventListener('focusin', pause)
        node.addEventListener('focusout', resume)
        document.addEventListener('visibilitychange', onVisibility)

        return () => {
            clearInterval(id)
            node.removeEventListener('pointerenter', pause)
            node.removeEventListener('pointerleave', resume)
            node.removeEventListener('focusin', pause)
            node.removeEventListener('focusout', resume)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [enabled, delay, loop, tilesToScroll, slidesLength, scrollRef])
}
